import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Plus, Trash2, Edit, ArrowUpDown, ArrowUp, ArrowDown, 
  LogOut, Building2, Home, Image, DollarSign, Percent,
  ChevronLeft, ChevronRight, X, Save, Upload, Archive, RotateCcw,
  FileSpreadsheet, Download, CheckCircle, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProperties() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!response.data.is_admin) {
        navigate("/");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      fetchProperties();
    }
  }, [user]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data);
    } catch (error) {
      logger.error("Failed to fetch properties:", error);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedProperties = [...properties]
    .filter(p => statusFilter === "all" || p.status === statusFilter)
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || "";
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const handleArchive = async (property) => {
    const newStatus = property.status === "archived" ? "available" : "archived";
    const action = newStatus === "archived" ? "archive" : "restore";
    if (!window.confirm(`Are you sure you want to ${action} this property?`)) return;
    
    try {
      const data = {
        title: property.title,
        description: property.description,
        price: property.price,
        location: property.location,
        market: property.market,
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        sqft: property.sqft,
        building_name: property.building_name || "",
        address: property.address,
        amenities: property.amenities || [],
        images: property.images || [],
        featured: property.featured,
        status: newStatus,
        monthly_common_charges: property.monthly_common_charges || 0,
        monthly_taxes: property.monthly_taxes || 0,
        projected_monthly_rent: property.projected_monthly_rent || 0,
      };
      await axios.put(`${API}/properties/${property.property_id}`, data, { withCredentials: true });
      toast.success(`Property ${action}d successfully`);
      fetchProperties();
    } catch (error) {
      toast.error(`Failed to ${action} property`);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}`, { withCredentials: true });
      toast.success("Property deleted");
      fetchProperties();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/admin");
    } catch (error) {
      logger.error("Logout error:", error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="w-4 h-4 text-[#0066FF]" />
      : <ArrowDown className="w-4 h-4 text-[#0066FF]" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1A1A2E] text-white sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-bold">
                1031<span className="text-[#60A5FA]">Condo</span>
              </Link>
              <span className="text-gray-400">|</span>
              <span className="text-sm text-gray-300">Property Management</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-300 hover:text-white">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">Property Inventory</h1>
            <p className="text-gray-500">{sortedProperties.length} of {properties.length} properties</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline"
              onClick={() => setBulkImportOpen(true)}
              className="rounded-lg"
              data-testid="bulk-import-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button 
              onClick={() => { setEditingProperty(null); setModalOpen(true); }}
              className="btn-primary rounded-lg"
              data-testid="add-property-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </div>
        </div>

        {/* Properties Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
                    Photo
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center gap-2">
                      Property <SortIcon field="title" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("market")}
                  >
                    <div className="flex items-center gap-2">
                      Market <SortIcon field="market" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("price")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Price <SortIcon field="price" />
                    </div>
                  </th>
                  <th 
                    className="text-center p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("sqft")}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Sq Ft <SortIcon field="sqft" />
                    </div>
                  </th>
                  <th 
                    className="text-center p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("bedrooms")}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Bed/Bath <SortIcon field="bedrooms" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("total_monthly_expenses")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Monthly Exp <SortIcon field="total_monthly_expenses" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("projected_monthly_rent")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Monthly Rent <SortIcon field="projected_monthly_rent" />
                    </div>
                  </th>
                  <th 
                    className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("cap_rate")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Cap Rate <SortIcon field="cap_rate" />
                    </div>
                  </th>
                  <th 
                    className="text-center p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Status <SortIcon field="status" />
                    </div>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedProperties.map((property) => (
                  <tr key={property.property_id} className="hover:bg-gray-50" data-testid={`property-row-${property.property_id}`}>
                    <td className="p-4">
                      <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-100">
                        {property.images?.[0] ? (
                          <img 
                            src={property.images[0]} 
                            alt={property.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-5 h-5 text-gray-300" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-semibold text-[#1A1A2E]">{property.title}</p>
                      <p className="text-sm text-gray-500">{property.location}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        property.market === "nyc" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"
                      }`}>
                        {property.market === "nyc" ? "NYC" : "Miami"}
                      </span>
                    </td>
                    <td className="p-4 text-right font-semibold text-[#1A1A2E]">
                      {formatCurrency(property.price)}
                    </td>
                    <td className="p-4 text-center text-gray-600">
                      {property.sqft?.toLocaleString()}
                    </td>
                    <td className="p-4 text-center text-gray-600">
                      {property.bedrooms} / {property.bathrooms}
                    </td>
                    <td className="p-4 text-right text-gray-600">
                      {formatCurrency(property.total_monthly_expenses)}
                    </td>
                    <td className="p-4 text-right text-green-600 font-medium">
                      {formatCurrency(property.projected_monthly_rent)}
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-semibold ${
                        (property.cap_rate || 0) >= 5 ? "text-green-600" : 
                        (property.cap_rate || 0) >= 3 ? "text-yellow-600" : "text-red-600"
                      }`}>
                        {property.cap_rate?.toFixed(2) || "0.00"}%
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        property.status === "available" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {property.status === "available" ? "Available" : "Archived"}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setEditingProperty(property); setModalOpen(true); }}
                          className="text-gray-500 hover:text-[#0066FF]"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleArchive(property)}
                          className={property.status === "archived" 
                            ? "text-gray-500 hover:text-green-600" 
                            : "text-gray-500 hover:text-yellow-600"
                          }
                          title={property.status === "archived" ? "Restore" : "Archive"}
                        >
                          {property.status === "archived" 
                            ? <RotateCcw className="w-4 h-4" />
                            : <Archive className="w-4 h-4" />
                          }
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(property.property_id)}
                          className="text-gray-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {properties.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">No Properties Yet</h3>
              <p className="text-gray-500 mb-4">Add your first property to get started</p>
              <Button onClick={() => setModalOpen(true)} className="btn-primary rounded-lg">
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Property Modal */}
      <PropertyFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        property={editingProperty}
        onSuccess={() => {
          fetchProperties();
          setEditingProperty(null);
        }}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onSuccess={fetchProperties}
      />
    </div>
  );
}

// Property Form Modal Component
function PropertyFormModal({ open, onOpenChange, property, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    location: "",
    market: "nyc",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    building_name: "",
    address: "",
    amenities: "",
    images: "",
    featured: false,
    status: "available",
    occupancy: "vacant",
    closing_time: "flexible",
    monthly_common_charges: "",
    monthly_taxes: "",
    projected_monthly_rent: "",
  });

  // Calculated values
  const monthlyExpenses = (parseFloat(formData.monthly_common_charges) || 0) + (parseFloat(formData.monthly_taxes) || 0);
  const annualExpenses = monthlyExpenses * 12;
  const annualRent = (parseFloat(formData.projected_monthly_rent) || 0) * 12;
  const annualNetIncome = annualRent - annualExpenses;
  const capRate = formData.price > 0 ? ((annualNetIncome / parseFloat(formData.price)) * 100) : 0;

  useEffect(() => {
    if (property) {
      setFormData({
        title: property.title || "",
        description: property.description || "",
        price: property.price?.toString() || "",
        location: property.location || "",
        market: property.market || "nyc",
        bedrooms: property.bedrooms?.toString() || "",
        bathrooms: property.bathrooms?.toString() || "",
        sqft: property.sqft?.toString() || "",
        building_name: property.building_name || "",
        address: property.address || "",
        amenities: property.amenities?.join(", ") || "",
        images: property.images?.join(", ") || "",
        featured: property.featured || false,
        status: property.status || "available",
        occupancy: property.occupancy || "vacant",
        closing_time: property.closing_time || "flexible",
        listing_type: property.listing_type || "individual",
        monthly_common_charges: property.monthly_common_charges?.toString() || "",
        monthly_taxes: property.monthly_taxes?.toString() || "",
        projected_monthly_rent: property.projected_monthly_rent?.toString() || "",
      });
    } else {
      setFormData({
        title: "", description: "", price: "", location: "", market: "nyc",
        bedrooms: "", bathrooms: "", sqft: "", building_name: "", address: "",
        amenities: "", images: "", featured: false, status: "available",
        occupancy: "vacant", closing_time: "flexible", listing_type: "individual",
        monthly_common_charges: "", monthly_taxes: "", projected_monthly_rent: "",
      });
    }
  }, [property, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      title: formData.title,
      description: formData.description,
      price: parseFloat(formData.price) || 0,
      location: formData.location,
      market: formData.market,
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseFloat(formData.bathrooms) || 0,
      sqft: parseInt(formData.sqft) || 0,
      building_name: formData.building_name,
      address: formData.address,
      amenities: formData.amenities.split(",").map(a => a.trim()).filter(Boolean),
      images: formData.images.split(",").map(i => i.trim()).filter(Boolean),
      featured: formData.featured,
      status: formData.status,
      occupancy: formData.occupancy,
      closing_time: formData.closing_time,
      listing_type: formData.listing_type,
      monthly_common_charges: parseFloat(formData.monthly_common_charges) || 0,
      monthly_taxes: parseFloat(formData.monthly_taxes) || 0,
      projected_monthly_rent: parseFloat(formData.projected_monthly_rent) || 0,
    };

    try {
      if (property) {
        await axios.put(`${API}/properties/${property.property_id}`, data, { withCredentials: true });
        toast.success("Property updated");
      } else {
        await axios.post(`${API}/properties`, data, { withCredentials: true });
        toast.success("Property created");
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error("Failed to save property");
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Append the new URL to existing images
      const currentImages = formData.images ? formData.images.split(",").map(i => i.trim()).filter(Boolean) : [];
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backendUrl}${response.data.url}`;
      currentImages.unshift(fullUrl);
      setFormData({ ...formData, images: currentImages.join(", ") });
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
      logger.error(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {property ? "Edit Property" : "Add New Property"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4">
          <div className="grid grid-cols-3 gap-6">
            {/* Left Column - Photo */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Photo</label>
              <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-3 border-2 border-dashed border-gray-300 relative">
                {formData.images.split(",")[0]?.trim() ? (
                  <img 
                    src={formData.images.split(",")[0].trim()} 
                    alt="Property"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                    <Image className="w-10 h-10 mb-2" />
                    <span className="text-sm">No image added</span>
                  </div>
                )}
              </div>
              
              {/* Upload Button */}
              <div className="mb-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="image-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload Image"}
                </Button>
              </div>
              
              {/* Or URL Input */}
              <div className="relative">
                <span className="absolute -top-2 left-3 bg-white px-2 text-xs text-gray-500">Or paste URL</span>
                <Input
                  placeholder="Image URL (comma-separated for multiple)"
                  value={formData.images}
                  onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                  className="text-sm"
                />
              </div>

              {/* Financial Summary Card */}
              <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Investment Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monthly Expenses:</span>
                    <span className="font-medium text-red-600">{formatCurrency(monthlyExpenses)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monthly Rent:</span>
                    <span className="font-medium text-green-600">{formatCurrency(parseFloat(formData.projected_monthly_rent) || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                    <span className="text-gray-500">Annual Net Income:</span>
                    <span className={`font-semibold ${annualNetIncome >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {formatCurrency(annualNetIncome)}
                    </span>
                  </div>
                  <div className="flex justify-between bg-blue-50 -mx-4 px-4 py-2 rounded">
                    <span className="font-semibold text-[#0066FF]">Cap Rate:</span>
                    <span className={`font-bold text-lg ${
                      capRate >= 5 ? "text-green-600" : capRate >= 3 ? "text-yellow-600" : "text-red-600"
                    }`}>
                      {capRate.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Columns - Details */}
            <div className="col-span-2 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., One57 Luxury Residence"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Market *</label>
                  <Select value={formData.market} onValueChange={(v) => setFormData({ ...formData, market: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nyc">New York City</SelectItem>
                      <SelectItem value="miami">Miami</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Midtown Manhattan"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full street address"
                    required
                  />
                </div>
              </div>

              {/* Building Info Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Building Name</label>
                  <Input
                    value={formData.building_name}
                    onChange={(e) => setFormData({ ...formData, building_name: e.target.value })}
                    placeholder="e.g., One57"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured</label>
                  <Select value={formData.featured ? "yes" : "no"} onValueChange={(v) => setFormData({ ...formData, featured: v === "yes" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes - Featured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Occupancy & Closing Time Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Occupancy</label>
                  <Select value={formData.occupancy} onValueChange={(v) => setFormData({ ...formData, occupancy: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacant">Vacant</SelectItem>
                      <SelectItem value="rented">Rented</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Closing Time</label>
                  <Select value={formData.closing_time} onValueChange={(v) => setFormData({ ...formData, closing_time: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30 days">30 Days</SelectItem>
                      <SelectItem value="60 days">60 Days</SelectItem>
                      <SelectItem value="90 days">90 Days</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Listing Type */}
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">Listing Type</label>
                <select
                  value={formData.listing_type}
                  onChange={(e) => setFormData({ ...formData, listing_type: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="listing-type-select"
                >
                  <option value="individual">Individual Unit</option>
                  <option value="bulk">Bulk Condo Units</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Bulk Condo Units are multiple units being sold as a group together.
                </p>
              </div>

              {/* Property Details */}
              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Listing Price *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="0"
                      className="pl-9"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sq Ft *</label>
                  <Input
                    type="number"
                    value={formData.sqft}
                    onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms *</label>
                  <Input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms *</label>
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              {/* Financial Details */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Common Charges</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData.monthly_common_charges}
                      onChange={(e) => setFormData({ ...formData, monthly_common_charges: e.target.value })}
                      placeholder="0"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Taxes</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData.monthly_taxes}
                      onChange={(e) => setFormData({ ...formData, monthly_taxes: e.target.value })}
                      placeholder="0"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Projected Monthly Rent</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData.projected_monthly_rent}
                      onChange={(e) => setFormData({ ...formData, projected_monthly_rent: e.target.value })}
                      placeholder="0"
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Property description..."
                  className="min-h-[80px]"
                />
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities (comma-separated)</label>
                <Input
                  value={formData.amenities}
                  onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                  placeholder="Pool, Gym, Concierge, Doorman"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="btn-primary" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Saving..." : property ? "Update Property" : "Add Property"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Bulk Import Modal Component
function BulkImportModal({ open, onOpenChange, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/properties/csv-template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'properties_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/properties/bulk-import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} properties`);
        onSuccess();
      }
      
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} rows had errors`);
      }
    } catch (error) {
      toast.error("Failed to import CSV");
      logger.error(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Bulk Import Properties
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">Instructions</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Download the CSV template below</li>
              <li>Fill in your property data (one row per property)</li>
              <li>Upload the completed CSV file</li>
            </ol>
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <p className="font-medium text-gray-700">CSV Template</p>
              <p className="text-sm text-gray-500">Download and fill in your property data</p>
            </div>
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload"
            />
            <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              {uploading ? "Processing CSV..." : "Upload your CSV file"}
            </p>
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading..." : "Select CSV File"}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex gap-4">
                <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{result.imported_count}</p>
                  <p className="text-sm text-green-600">Imported</p>
                </div>
                <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{result.error_count}</p>
                  <p className="text-sm text-red-600">Errors</p>
                </div>
              </div>

              {/* Imported Properties */}
              {result.imported.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Imported Properties</h4>
                  <div className="max-h-32 overflow-y-auto text-sm text-green-700">
                    {result.imported.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3" />
                        Row {item.row}: {item.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Errors</h4>
                  <div className="max-h-32 overflow-y-auto text-sm text-red-700">
                    {result.errors.map((error, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <AlertCircle className="w-3 h-3" />
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CSV Format Reference */}
          <div className="text-xs text-gray-500">
            <p className="font-medium mb-1">Required columns:</p>
            <p>title, price, location, market (nyc/miami), bedrooms, bathrooms, sqft, address</p>
            <p className="font-medium mt-2 mb-1">Optional columns:</p>
            <p>description, building_name, amenities, images, featured, status, monthly_common_charges, monthly_taxes, projected_monthly_rent</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
